/**
 * TeamInviteWizard
 * --------------------------------------------------------------------------
 * Admin-only bulk-invite modal. Two tabs:
 *   • Manual   — repeatable rows (email, name, role, department)
 *   • CSV      — paste or upload a CSV; parsed client-side; shows preview
 *
 * On Submit:
 *   POST /api/admin/team/invite { rows: [...] }
 *   → server responds with per-row status and a one-time temp password.
 *   We render the results as a copyable table so the admin can hand out
 *   credentials before we forget them (temp passwords are NEVER stored
 *   anywhere else — this is the only chance to see them).
 */
import React, { useState, useRef, useCallback } from 'react';
import {
  Users, Upload, Plus, Trash2, Copy, CheckCircle2, AlertCircle,
  FileDown, Send, Loader2, X, FileSpreadsheet, UserPlus,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const ROLES = [
  { value: 'employee', label: 'Employee' },
  { value: 'safety_officer', label: 'Safety Officer' },
  { value: 'safety_manager', label: 'Safety Manager' },
  { value: 'investigator', label: 'Investigator' },
  { value: 'auditor', label: 'Auditor' },
  { value: 'trainer', label: 'Trainer' },
  { value: 'inspector', label: 'Inspector (read-only)' },
  { value: 'admin', label: 'Administrator' },
];

const emptyRow = () => ({ email: '', name: '', role: 'employee', department: '' });

// Very forgiving CSV parser — handles quoted values with commas, CRLF, comment lines.
function parseCsv(text) {
  const rows = [];
  const lines = text.replace(/\r\n?/g, '\n').split('\n');
  if (lines.length === 0) return rows;
  // Detect header
  const header = lines[0].split(',').map((c) => c.trim().toLowerCase().replace(/^"|"$/g, ''));
  const idx = {
    email: header.indexOf('email'),
    name: header.indexOf('name'),
    role: header.indexOf('role'),
    department: header.indexOf('department'),
  };
  const hasHeader = idx.email >= 0 || idx.name >= 0;
  const startAt = hasHeader ? 1 : 0;
  const cols = hasHeader ? idx : { email: 0, name: 1, role: 2, department: 3 };
  for (let i = startAt; i < lines.length; i++) {
    const raw = lines[i];
    if (!raw.trim() || raw.trim().startsWith('#') || raw.trim().startsWith(',#')) continue;
    // naive split — works for typical CSVs; handle quoted commas minimally
    const parts = [];
    let cur = ''; let inQ = false;
    for (const ch of raw) {
      if (ch === '"') { inQ = !inQ; continue; }
      if (ch === ',' && !inQ) { parts.push(cur); cur = ''; continue; }
      cur += ch;
    }
    parts.push(cur);
    const g = (n) => (n >= 0 && n < parts.length ? parts[n].trim() : '');
    rows.push({
      email: g(cols.email),
      name: g(cols.name),
      role: (g(cols.role) || 'employee').toLowerCase(),
      department: g(cols.department),
    });
  }
  return rows;
}

export default function TeamInviteWizard({ token, trigger }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('manual');
  const [rows, setRows] = useState([emptyRow(), emptyRow(), emptyRow()]);
  const [csvText, setCsvText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null); // {summary, results: [...]}
  const fileRef = useRef(null);

  const setRow = (i, patch) => setRows((rs) => rs.map((r, ix) => (ix === i ? { ...r, ...patch } : r)));
  const addRow = () => setRows((rs) => [...rs, emptyRow()]);
  const removeRow = (i) => setRows((rs) => (rs.length > 1 ? rs.filter((_, ix) => ix !== i) : rs));

  const importCsv = useCallback((text) => {
    const parsed = parseCsv(text);
    if (parsed.length === 0) {
      toast.warning('No rows found in that CSV.');
      return;
    }
    setRows(parsed);
    setTab('manual'); // let admin double-check what was parsed
    toast.success(`Imported ${parsed.length} row${parsed.length === 1 ? '' : 's'}. Review, then Send Invites.`);
  }, []);

  const onFilePick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const text = await f.text();
    setCsvText(text);
    importCsv(text);
  };

  const downloadTemplate = async () => {
    try {
      const r = await fetch(`${API_BASE}/api/admin/team/csv-template`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'aeroguard_team_invite_template.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) {
      toast.error(`Template download failed: ${e.message}`);
    }
  };

  const send = async () => {
    // Trim empty rows before sending
    const clean = rows.filter((r) => (r.email || '').trim() || (r.name || '').trim());
    if (clean.length === 0) {
      toast.warning('Add at least one row with an email and name.');
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(`${API_BASE}/api/admin/team/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ rows: clean }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${r.status}`);
      }
      const j = await r.json();
      setResults(j);
      const { created, skipped, errors } = j.summary;
      if (created > 0) {
        toast.success(`${created} team member${created === 1 ? '' : 's'} invited.`, {
          description:
            errors + skipped > 0
              ? `${skipped} skipped, ${errors} errors — see the results table.`
              : 'Copy the temporary passwords below and share them securely.',
        });
      } else {
        toast.warning('No new users were created — check the errors below.');
      }
    } catch (e) {
      toast.error(`Invite failed: ${e.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setResults(null);
    setRows([emptyRow(), emptyRow(), emptyRow()]);
    setCsvText('');
  };

  const copyRow = (row) => {
    const text = `Email: ${row.email}\nName: ${row.name}\nRole: ${row.role}\nTemporary password: ${row.temp_password}\n\nPlease change your password after first login.`;
    navigator.clipboard.writeText(text).then(
      () => toast.success('Credentials copied to clipboard.'),
      () => toast.error('Clipboard copy failed.'),
    );
  };

  const copyAll = () => {
    const created = (results?.results || []).filter((r) => r.status === 'created');
    if (created.length === 0) return;
    const text = created.map((r) =>
      `${r.email}\t${r.name}\t${r.role}\t${r.temp_password}`,
    ).join('\n');
    navigator.clipboard.writeText(text).then(
      () => toast.success(`Copied ${created.length} credential row${created.length === 1 ? '' : 's'} (tab-separated).`),
      () => toast.error('Clipboard copy failed.'),
    );
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        {trigger || (
          <Button className="bg-cyan-600 hover:bg-cyan-700" data-testid="open-team-invite-wizard">
            <UserPlus className="w-4 h-4 mr-2" />
            Invite Team
          </Button>
        )}
      </DialogTrigger>
      <DialogContent
        className="bg-slate-950 border-slate-800 text-slate-100 sm:max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="team-invite-wizard"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-cyan-400" />
            Invite your team
          </DialogTitle>
          <p className="text-[12px] text-slate-400">
            Add colleagues one-by-one or paste a CSV. Each person gets a temporary password you
            share once — they change it on first login.
          </p>
        </DialogHeader>

        {!results ? (
          <div className="space-y-4 pt-2">
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="bg-slate-900 border border-slate-800">
                <TabsTrigger value="manual" data-testid="tab-manual">
                  <UserPlus className="w-3.5 h-3.5 mr-1.5" /> One by one
                </TabsTrigger>
                <TabsTrigger value="csv" data-testid="tab-csv">
                  <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" /> CSV upload
                </TabsTrigger>
              </TabsList>

              <TabsContent value="manual" className="mt-3 space-y-2" data-testid="invite-manual-panel">
                <div className="grid grid-cols-12 gap-2 text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-1">
                  <div className="col-span-4">Email *</div>
                  <div className="col-span-3">Name *</div>
                  <div className="col-span-3">Role</div>
                  <div className="col-span-2">Dept</div>
                </div>
                <div className="space-y-1.5 max-h-[340px] overflow-y-auto pr-1">
                  {rows.map((r, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`invite-row-${i}`}>
                      <Input
                        value={r.email}
                        onChange={(e) => setRow(i, { email: e.target.value })}
                        placeholder="person@company.com"
                        className="col-span-4 bg-slate-900 border-slate-700 h-9 text-[13px]"
                        data-testid={`invite-email-${i}`}
                      />
                      <Input
                        value={r.name}
                        onChange={(e) => setRow(i, { name: e.target.value })}
                        placeholder="Full name"
                        className="col-span-3 bg-slate-900 border-slate-700 h-9 text-[13px]"
                        data-testid={`invite-name-${i}`}
                      />
                      <div className="col-span-3">
                        <Select value={r.role} onValueChange={(v) => setRow(i, { role: v })}>
                          <SelectTrigger
                            className="bg-slate-900 border-slate-700 h-9 text-[13px]"
                            data-testid={`invite-role-${i}`}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                            {ROLES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2 flex items-center gap-1">
                        <Input
                          value={r.department}
                          onChange={(e) => setRow(i, { department: e.target.value })}
                          placeholder="Optional"
                          className="bg-slate-900 border-slate-700 h-9 text-[13px]"
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeRow(i)}
                          disabled={rows.length === 1}
                          className="h-9 w-9 text-slate-500 hover:text-rose-400"
                          title="Remove row"
                          data-testid={`invite-remove-${i}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
                <Button
                  variant="outline"
                  onClick={addRow}
                  className="bg-slate-900 border-slate-700 text-slate-200 hover:bg-slate-800"
                  data-testid="invite-add-row"
                >
                  <Plus className="w-3.5 h-3.5 mr-1.5" /> Add another
                </Button>
              </TabsContent>

              <TabsContent value="csv" className="mt-3 space-y-3" data-testid="invite-csv-panel">
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    variant="outline"
                    onClick={() => fileRef.current?.click()}
                    className="bg-slate-900 border-slate-700 text-slate-200 hover:bg-slate-800"
                    data-testid="invite-csv-upload"
                  >
                    <Upload className="w-3.5 h-3.5 mr-1.5" /> Upload CSV file
                  </Button>
                  <input
                    ref={fileRef}
                    type="file"
                    accept=".csv,text/csv"
                    onChange={onFilePick}
                    className="hidden"
                    data-testid="invite-csv-file-input"
                  />
                  <Button
                    variant="ghost"
                    onClick={downloadTemplate}
                    className="text-cyan-300 hover:bg-cyan-500/10"
                    data-testid="invite-csv-download-template"
                  >
                    <FileDown className="w-3.5 h-3.5 mr-1.5" /> Download template
                  </Button>
                  <span className="text-[11px] text-slate-500">
                    Columns: <code className="font-mono">email, name, role, department</code>
                  </span>
                </div>
                <div>
                  <Label htmlFor="csv-paste" className="text-[12px] text-slate-400">…or paste CSV text here</Label>
                  <Textarea
                    id="csv-paste"
                    value={csvText}
                    onChange={(e) => setCsvText(e.target.value)}
                    placeholder={'email,name,role,department\ncaptain.smith@example.com,Sarah Smith,safety_manager,Flight Ops\n…'}
                    rows={7}
                    className="bg-slate-900 border-slate-700 mt-1 font-mono text-[12px]"
                    data-testid="invite-csv-textarea"
                  />
                  <div className="flex justify-end mt-2">
                    <Button
                      onClick={() => importCsv(csvText)}
                      disabled={!csvText.trim()}
                      className="bg-cyan-600 hover:bg-cyan-700"
                      data-testid="invite-csv-parse"
                    >
                      Parse & preview
                    </Button>
                  </div>
                </div>
              </TabsContent>
            </Tabs>

            <div className="flex items-center justify-between pt-3 border-t border-slate-800">
              <p className="text-[11px] text-slate-500">
                Max 200 invites per batch. Duplicates and existing users are skipped automatically.
              </p>
              <Button
                onClick={send}
                disabled={submitting}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="invite-send"
              >
                {submitting
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>
                  : <><Send className="w-4 h-4 mr-2" /> Send invites</>}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4 pt-2" data-testid="invite-results">
            {/* Summary strip */}
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-md border border-emerald-500/40 bg-emerald-500/10 p-3">
                <div className="text-[11px] text-emerald-300 uppercase tracking-wider">Created</div>
                <div className="text-2xl font-bold text-emerald-100" data-testid="invite-summary-created">{results.summary.created}</div>
              </div>
              <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3">
                <div className="text-[11px] text-amber-300 uppercase tracking-wider">Skipped</div>
                <div className="text-2xl font-bold text-amber-100" data-testid="invite-summary-skipped">{results.summary.skipped}</div>
              </div>
              <div className="rounded-md border border-rose-500/40 bg-rose-500/10 p-3">
                <div className="text-[11px] text-rose-300 uppercase tracking-wider">Errors</div>
                <div className="text-2xl font-bold text-rose-100" data-testid="invite-summary-errors">{results.summary.errors}</div>
              </div>
            </div>

            {results.summary.created > 0 && (
              <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-[12px] text-amber-100">
                <b>Copy the temporary passwords now.</b> They are shown once and never stored in plain text. Ask each user to sign in and change their password immediately.
              </div>
            )}

            <div className="rounded-md border border-slate-800 max-h-[340px] overflow-y-auto">
              <table className="w-full text-[12px]">
                <thead className="sticky top-0 bg-slate-900 border-b border-slate-800 text-slate-400 uppercase tracking-wider text-[10px]">
                  <tr>
                    <th className="px-2 py-1.5 text-left">Status</th>
                    <th className="px-2 py-1.5 text-left">Email</th>
                    <th className="px-2 py-1.5 text-left">Name / Role</th>
                    <th className="px-2 py-1.5 text-left">Temp password / Error</th>
                    <th className="px-2 py-1.5"></th>
                  </tr>
                </thead>
                <tbody>
                  {(results.results || []).map((r, i) => (
                    <tr key={i} className="border-b border-slate-800/60 hover:bg-slate-900/40" data-testid={`invite-result-${i}`}>
                      <td className="px-2 py-1.5 align-top">
                        {r.status === 'created' && (
                          <Badge className="bg-emerald-500/20 text-emerald-200 border-emerald-500/40">
                            <CheckCircle2 className="w-3 h-3 mr-1" /> Created
                          </Badge>
                        )}
                        {r.status === 'skipped' && (
                          <Badge className="bg-amber-500/20 text-amber-200 border-amber-500/40">Skipped</Badge>
                        )}
                        {r.status === 'error' && (
                          <Badge className="bg-rose-500/20 text-rose-200 border-rose-500/40">
                            <AlertCircle className="w-3 h-3 mr-1" /> Error
                          </Badge>
                        )}
                      </td>
                      <td className="px-2 py-1.5 align-top font-mono text-slate-200">{r.email || '—'}</td>
                      <td className="px-2 py-1.5 align-top">
                        {r.name && <div className="text-slate-200">{r.name}</div>}
                        {r.role && <div className="text-[10px] text-slate-500">{r.role}</div>}
                      </td>
                      <td className="px-2 py-1.5 align-top">
                        {r.temp_password ? (
                          <code
                            className="font-mono text-emerald-300 bg-slate-900 px-1.5 py-0.5 rounded select-all"
                            data-testid={`invite-temp-password-${i}`}
                          >
                            {r.temp_password}
                          </code>
                        ) : (
                          <span className="text-rose-300 text-[11px]">{r.error || '—'}</span>
                        )}
                      </td>
                      <td className="px-2 py-1.5 align-top">
                        {r.temp_password && (
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-6 w-6 text-slate-400 hover:text-cyan-300"
                            onClick={() => copyRow(r)}
                            title="Copy credentials"
                            data-testid={`invite-copy-${i}`}
                          >
                            <Copy className="w-3 h-3" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between pt-2 border-t border-slate-800">
              <Button
                variant="ghost"
                onClick={copyAll}
                disabled={results.summary.created === 0}
                className="text-cyan-300 hover:bg-cyan-500/10"
                data-testid="invite-copy-all"
              >
                <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy all credentials
              </Button>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={reset}
                  className="bg-slate-900 border-slate-700 text-slate-200 hover:bg-slate-800"
                  data-testid="invite-add-more"
                >
                  Invite more people
                </Button>
                <Button
                  onClick={() => setOpen(false)}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="invite-done"
                >
                  Done
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
